<template>
  <div class="chiplist-wrapper" :class="{ '__hide-toggle': hideToggle }">
    <template v-if="items && items.length">
      <div class="chip-list" :class="{ 'chip-list--text': isTextVariant }">
        <span
          v-for="(entry, idx) in renderItems"
          :key="idx"
          class="chip-item"
          :class="variantClass"
        >
          <span v-b-tooltip.hover.top :title="displayText(entry.item)" class="chip-text">
            {{ entry.text }}
          </span>
          <button
            v-if="attachmentUrl(entry.item)"
            v-b-tooltip.hover.bottom
            class="chip-attachment"
            :title="openDocumentTooltip"
            @mousedown.prevent
            @click.prevent.stop="handleOpenAttachment(attachmentUrl(entry.item))"
          >
            <i class="icon-outline_file-text"></i>
          </button>
        </span>

        <span
          v-if="hasOverflow && (!showAll || isDropdownMode)"
          v-b-tooltip.hover.top
          :title="hiddenItemsTooltip"
          class="chip-item more-count"
          :class="{ __clickable: canOpenDropdownFromCount }"
          @click.stop="handleMoreCountClick"
          >+{{ items.length - maxVisible }}
        </span>
      </div>

      <div v-if="shouldShowActionButton" class="chip-actions">
        <button class="toggle-chips" type="button" @click="toggle">
          {{ toggleButtonLabel }}
          <i
            :class="['chev', showAll ? 'icon-outline_chevron-up' : 'icon-outline_chevron-down']"
          ></i>
        </button>
      </div>

      <div
        v-if="isDropdownMode && showAll"
        ref="dropdown"
        class="chip-dropdown"
        :style="dropdownStyle"
        @click.stop
      >
        <div class="chip-dropdown__title">{{ dropdownTitle }}</div>
        <div class="chip-dropdown__list">
          <div v-for="(item, idx) in items" :key="`dropdown-${idx}`" class="chip-dropdown__item">
            <span v-b-tooltip.hover.top :title="displayText(item)" class="chip-dropdown__text">{{
              displayText(item)
            }}</span>
            <button
              v-if="attachmentUrl(item)"
              v-b-tooltip.hover.bottom
              class="chip-dropdown__attachment"
              :title="openDocumentTooltip"
              @mousedown.prevent
              @click.prevent.stop="handleOpenAttachment(attachmentUrl(item))"
            >
              <i class="icon-outline_file-text"></i>
            </button>
          </div>
        </div>
      </div>
    </template>
    <span v-else>-</span>
  </div>
</template>

<script>
export default {
  name: 'McChipList',
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    maxVisible: {
      type: Number,
      default: 3,
    },
    itemTextField: {
      type: String,
      default: '',
    },
    itemAttachmentUrlField: {
      type: String,
      default: '',
    },
    hideToggle: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String,
      default: '',
      validator: (value) =>
        ['', 'text', 'primary', 'success', 'danger', 'warning', 'outline-secondary'].includes(
          value
        ),
    },
    showAllMode: {
      type: String,
      default: 'inline',
      validator: (value) => ['inline', 'dropdown'].includes(value),
    },
    showAllDropdownTitle: {
      type: String,
      default: '',
    },
    showAllDropdownTrigger: {
      type: String,
      default: 'both',
      validator: (value) => ['action', 'count', 'both'].includes(value),
    },
    truncateTextLength: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      showAll: false,
      dropdownAnchorEl: null,
      dropdownPosition: {
        top: 0,
        left: 0,
        width: 280,
      },
    }
  },
  computed: {
    hasOverflow() {
      return (this.items || []).length > this.maxVisible
    },
    isDropdownMode() {
      return this.showAllMode === 'dropdown'
    },
    canOpenDropdownFromCount() {
      if (!this.isDropdownMode) return false
      return ['count', 'both'].includes(this.showAllDropdownTrigger)
    },
    canOpenDropdownFromAction() {
      if (!this.isDropdownMode) return true
      return ['action', 'both'].includes(this.showAllDropdownTrigger)
    },
    shouldShowActionButton() {
      if (!this.hasOverflow || this.hideToggle) return false
      return this.canOpenDropdownFromAction
    },
    toggleButtonLabel() {
      return this.showAll ? this.$t('global.action.hide') : this.$t('global.showAll')
    },
    dropdownTitle() {
      const baseTitle = this.showAllDropdownTitle || this.$t('global.object.data')
      return `${baseTitle} (${(this.items || []).length})`
    },
    openDocumentTooltip() {
      return this.$t('global.action.openDocumentInNewTab')
    },
    visibleItems() {
      if (this.isDropdownMode || !this.showAll) {
        return (this.items || []).slice(0, this.maxVisible)
      }
      return this.items
    },
    renderItems() {
      return this.visibleItems.map((item) => ({ item, text: this.truncateText(item) }))
    },
    dropdownStyle() {
      return {
        top: `${this.dropdownPosition.top}px`,
        left: `${this.dropdownPosition.left}px`,
        width: `${this.dropdownPosition.width}px`,
      }
    },
    variantClass() {
      return this.variant ? `chip-item--${this.variant}` : ''
    },
    isTextVariant() {
      return this.variant === 'text'
    },
    hiddenItemsTooltip() {
      if (!this.hasOverflow) return ''
      return this.items
        .slice(this.maxVisible)
        .map((item) => this.displayText(item))
        .join(', ')
    },
  },
  watch: {
    showAllMode() {
      this.showAll = false
      this.dropdownAnchorEl = null
    },
    showAllDropdownTrigger() {
      this.showAll = false
      this.dropdownAnchorEl = null
    },
    showAll(value) {
      if (!this.isDropdownMode) return
      if (value) {
        this.$nextTick(() => {
          this.attachDropdownToBody()
          this.updateDropdownPosition()
          this.bindDropdownListeners()
        })
        return
      }
      this.unbindDropdownListeners()
      this.dropdownAnchorEl = null
    },
    items() {
      if (!this.hasOverflow) this.showAll = false
    },
    maxVisible() {
      if (!this.hasOverflow) this.showAll = false
    },
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick)
    this.unbindDropdownListeners()
    this.cleanupDropdownNode()
  },
  methods: {
    displayText(item) {
      if (this.itemTextField) {
        return item && item[this.itemTextField] ? item[this.itemTextField] : '-'
      }
      return item
    },
    truncateText(item) {
      const text = this.displayText(item)
      if (!text || text === '-') return text
      if (!this.truncateTextLength || this.truncateTextLength <= 0) return text
      return text.length > this.truncateTextLength
        ? `${text.slice(0, this.truncateTextLength)}...`
        : text
    },
    attachmentUrl(item) {
      if (!this.itemAttachmentUrlField) return ''
      return item && item[this.itemAttachmentUrlField] ? item[this.itemAttachmentUrlField] : ''
    },
    toggle(event) {
      if (!this.canOpenDropdownFromAction) return
      this.dropdownAnchorEl = event?.currentTarget || this.$el
      this.showAll = !this.showAll
    },
    handleMoreCountClick(event) {
      if (!this.canOpenDropdownFromCount) return
      this.dropdownAnchorEl = event?.currentTarget || this.$el
      this.showAll = !this.showAll
    },
    handleOutsideClick(event) {
      if (!this.isDropdownMode || !this.showAll) return
      const dropdownEl = this.$refs.dropdown
      const isInsideTrigger = this.$el && this.$el.contains(event.target)
      const isInsideDropdown = dropdownEl && dropdownEl.contains(event.target)

      if (!isInsideTrigger && !isInsideDropdown) {
        this.showAll = false
      }
    },
    attachDropdownToBody() {
      const dropdownEl = this.$refs.dropdown
      if (!dropdownEl) return
      if (dropdownEl.parentNode !== document.body) {
        document.body.appendChild(dropdownEl)
      }
    },
    cleanupDropdownNode() {
      const dropdownEl = this.$refs.dropdown
      if (!dropdownEl) return
      if (dropdownEl.parentNode === document.body) {
        document.body.removeChild(dropdownEl)
      }
    },
    bindDropdownListeners() {
      window.addEventListener('resize', this.updateDropdownPosition)
      window.addEventListener('scroll', this.updateDropdownPosition, true)
    },
    unbindDropdownListeners() {
      window.removeEventListener('resize', this.updateDropdownPosition)
      window.removeEventListener('scroll', this.updateDropdownPosition, true)
    },
    updateDropdownPosition() {
      if (!this.isDropdownMode || !this.showAll || !this.$el) return

      const dropdownEl = this.$refs.dropdown
      if (!dropdownEl) return

      const anchorEl =
        this.dropdownAnchorEl && document.body.contains(this.dropdownAnchorEl)
          ? this.dropdownAnchorEl
          : this.$el
      const triggerRect = anchorEl.getBoundingClientRect()
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight
      const dropdownWidth = Math.max(triggerRect.width, 280)

      this.dropdownPosition.width = dropdownWidth

      this.$nextTick(() => {
        const currentHeight = dropdownEl.offsetHeight || 0
        const spacing = 8
        const horizontalOffset = -3

        let top = triggerRect.bottom + spacing
        let left = triggerRect.left + horizontalOffset

        if (top + currentHeight > viewportHeight - spacing) {
          top = Math.max(spacing, triggerRect.top - currentHeight - spacing)
        }

        if (left + dropdownWidth > viewportWidth - spacing) {
          left = Math.max(spacing, viewportWidth - dropdownWidth - spacing)
        }

        this.dropdownPosition = {
          top,
          left,
          width: dropdownWidth,
        }
      })
    },
    handleOpenAttachment(url) {
      if (url) {
        window.open(url, '_blank')
      }
    },
  },
}
</script>

<style scoped lang="scss">
.chiplist-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  &.__hide-toggle {
    flex-direction: row;
    align-items: center;
    gap: 0;
  }
}

.chip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.chip-list--text {
  gap: 0;

  .chip-item--text {
    display: inline;
    background: transparent;
    color: inherit;
    border-radius: 0;
    font-size: inherit;
    height: auto;
    overflow: visible;
    flex-shrink: initial;
  }

  .chip-item--text + .chip-item--text::before {
    content: ', ';
  }

  .chip-item--text .chip-text {
    display: inline;
    padding: 0;
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
  }

  .more-count {
    margin-left: 4px;
  }
}

.chip-item {
  display: inline-flex;
  align-items: center;
  background: $blue;
  color: $white;
  border-radius: 18px;
  font-size: 12px;
  overflow: hidden;
  height: 25px;
  flex-shrink: 0;

  &--primary {
    background: $blue;
  }

  &--success {
    background: $green;
  }

  &--danger {
    background: $red;
  }

  &--warning {
    background: $orange;
  }

  &--outline-secondary {
    background: white;
    border: 1px solid #cdd3df;
    color: #384157;
  }
}

.chip-text {
  padding: 0 14px;
  display: flex;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chip-attachment {
  width: 34px;
  height: 100%;
  border: none;
  border-left: 2px solid $white;
  background: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}

.chip-attachment i::before {
  font-size: 16px;
  color: $white;
}

.chip-item.more-count {
  background: $green;
  min-width: 25px;
  height: 25px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  padding: 0 8px;
  font-weight: 600;
  flex-shrink: 0;
}

.chip-item.more-count.__clickable {
  cursor: pointer;
}

.chip-actions {
  margin-top: 6px;
  width: 100%;
}

.toggle-chips {
  background: transparent;
  border: none;
  color: $cyan;
  cursor: pointer;
  padding: 0;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}

.chip-actions .toggle-chips {
  display: inline-block;
  margin-left: 0;
}

.chev::before {
  position: absolute;
  font-size: 16px;
  padding-top: 2px !important;
  font-weight: 500;
}

.chip-dropdown {
  position: fixed;
  display: flex;
  flex-direction: column;
  min-width: 280px;
  max-width: 420px;
  max-height: 260px;
  overflow: hidden;
  background: $white;
  border: 1px solid #e6e7ec;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(52, 66, 100, 0.12);
  z-index: 1060;
}

.chip-dropdown__title {
  padding: 14px 16px;
  font-size: 14px;
  font-weight: 600;
  color: #344264;
}

.chip-dropdown__list {
  padding: 0 8px 4px 8px;
  overflow-y: auto;
  min-height: 0;
}

.chip-dropdown__item {
  min-height: 44px;
  padding: 0 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e6e7ec;
  gap: 8px;
}

.chip-dropdown__item:last-child {
  border-bottom: none;
}

.chip-dropdown__text {
  color: #344264;
  font-size: 12px;
  line-height: 20px;
  word-break: break-word;
}

.chip-dropdown__attachment {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: #f2f4f7;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.chip-dropdown__attachment i::before {
  font-size: 14px;
  color: #344264;
}
</style>
